import React, { useState } from 'react';
import { Menu, X, User, Heart, ShoppingCart, Search } from 'lucide-react';

export default function NavbarPreview({ attributes = {}, editMode = false, onAttributeChange, themeColors, previewDevice = 'desktop' }) {
  const {
    logoUrl = '',
    brandName = 'BRAND',
    navLinks = [
      { label: 'Home', path: '/' },
      { label: 'Shop', path: '/shop' },
      { label: 'About', path: '/about' },
      { label: 'Contact', path: '/contact' },
      { label: 'Orders', path: '/orders' },
    ],
    showUserProfile = true,
    showCartIcon = true,
    showWishlistIcon = true,
  } = attributes;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Extract colors from theme, with smart fallbacks
  // Note: themeColors.text may be a nested object { primary, secondary, ... } or a string
  const bgColor = themeColors?.background || '#FFFFFF';
  const textColor = (typeof themeColors?.text === 'object' ? themeColors?.text?.primary : themeColors?.text) || '#1A1A1A';
  const accentColor = themeColors?.primary || themeColors?.accent || '#2D6A4F';
  const borderColor = themeColors?.border || '#E5E7EB';
  const surfaceColor = themeColors?.surface || '#F9FAFB';

  // Detect if dark theme (background is dark)
  const isDark = bgColor && ['#0', '#1', '#2'].some(p => bgColor.toLowerCase().startsWith(p));
  const navBg = isDark ? bgColor : '#FFFFFF';
  const navText = isDark ? '#FFFFFF' : textColor;
  const navBorder = isDark ? 'rgba(255,255,255,0.1)' : borderColor;
  const navIconColor = isDark ? 'rgba(255,255,255,0.7)' : textColor;
  const navLinkSeparator = isDark ? 'rgba(255,255,255,0.05)' : surfaceColor;

  const isMobile = previewDevice === 'mobile';
  const isTablet = previewDevice === 'tablet';

  // Mobile: hamburger menu layout
  if (isMobile) {
    return (
      <nav style={{
        width: '100%', background: navBg, borderBottom: `1px solid ${navBorder}`,
        fontFamily: 'Inter, system-ui, sans-serif', position: 'relative', zIndex: 100,
      }}>
        <div style={{
          padding: '0 16px', height: '56px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{
              background: 'none', border: 'none', cursor: 'default',
              color: navText, padding: '4px', display: 'flex',
            }}>
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} style={{ height: '28px', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 700, color: navText, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {brandName || 'BRAND'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {showCartIcon && <ShoppingCart size={18} style={{ color: navIconColor }} />}
            {showUserProfile && <User size={18} style={{ color: navIconColor }} />}
          </div>
        </div>
        {mobileMenuOpen && (
          <div style={{
            padding: '8px 16px 16px', borderTop: `1px solid ${navBorder}`,
            background: navBg,
            display: 'flex', flexDirection: 'column', gap: '12px',
          }}>
            {navLinks.map((link, i) => (
              <span key={i} style={{
                fontSize: '14px', fontWeight: 500, color: navText,
                padding: '8px 0', borderBottom: `1px solid ${navLinkSeparator}`, cursor: 'default',
              }}>
                {link.label}
              </span>
            ))}
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav style={{
      width: '100%',
      background: navBg,
      borderBottom: `1px solid ${navBorder}`,
      padding: isTablet ? '0 16px' : '0 24px',
      height: isTablet ? '56px' : '64px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
      zIndex: 100,
    }}>
      {/* Left - Logo/Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>
        {logoUrl ? (
          <img src={logoUrl} alt={brandName} style={{ height: isTablet ? '30px' : '36px', objectFit: 'contain' }} />
        ) : (
          <span style={{
            fontSize: isTablet ? '17px' : '20px',
            fontWeight: 700,
            color: navText,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}>
            {brandName || 'BRAND'}
          </span>
        )}
      </div>

      {/* Center - Nav Links */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isTablet ? '20px' : '32px',
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
      }}>
        {navLinks.map((link, i) => (
          <span key={i} style={{
            fontSize: isTablet ? '11px' : '12px',
            fontWeight: 500,
            color: navText,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            cursor: 'default',
            opacity: 0.8,
          }}>
            {link.label}
          </span>
        ))}
      </div>

      {/* Right - Icons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isTablet ? '12px' : '16px', flex: '0 0 auto' }}>
        {showUserProfile && (
          <div style={{ color: navIconColor, cursor: 'default' }}>
            <User size={isTablet ? 18 : 20} />
          </div>
        )}
        {showWishlistIcon && (
          <div style={{ position: 'relative', color: navIconColor, cursor: 'default' }}>
            <Heart size={isTablet ? 18 : 20} />
            <span style={{
              position: 'absolute', top: '-6px', right: '-8px',
              background: accentColor, color: '#fff', fontSize: '9px',
              fontWeight: 700, borderRadius: '50%', width: '16px', height: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>3</span>
          </div>
        )}
        {showCartIcon && (
          <div style={{ color: navIconColor, cursor: 'default' }}>
            <ShoppingCart size={isTablet ? 18 : 20} />
          </div>
        )}
      </div>
    </nav>
  );
}
